import { FUNCTIONS_BASE, supabase } from "./supabase";

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return {
    Authorization: `Bearer ${token}`,
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    "Content-Type": "application/json",
  };
}

async function call<T>(name: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    ...init,
    headers: { ...headers, ...(init?.headers ?? {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || `Request failed (${res.status})`);
  }
  return json as T;
}

export type DateRange = { from: string; to: string }; // YYYY-MM-DD

export type PlatformUserBucket = {
  total: number;
  test: number;
  organic: number;
};

export type Metrics = {
  generated_at: string;
  range: { from: string; to: string };
  data_quality_notes?: string[];
  closed_testing: {
    start: string;
    end: string;
    required_days: number;
    days_elapsed: number;
    days_remaining: number;
    complete: boolean;
    label: string;
  };
  users: {
    total: number;
    signups_in_range: number;
    active_in_range: number;
    signups_7d: number;
    signups_30d: number;
    active_7d: number;
    active_30d: number;
    providers: Record<string, number>;
    with_games: number;
    test_count?: number;
    organic_count?: number;
    by_platform?: Record<string, PlatformUserBucket>;
  };
  games: {
    total: number;
    created_in_range: number;
    by_status: Record<string, number>;
    by_species: Record<string, number>;
  };
  trends: {
    signups_by_day: Array<{ day: string; count: number }>;
    games_by_day: Array<{ day: string; count: number }>;
    revenue_by_day: Array<{ day: string; usd: number }>;
  };
  auth_errors: Array<{
    id: string;
    created_at: string;
    event: string;
    platform: string | null;
    error_code: string | null;
    error_message: string | null;
  }>;
  auth_errors_by_platform: Record<string, number>;
  feature_requests: {
    total: number;
    recent: Array<{
      id: string;
      suggestion: string;
      created_at: string;
      user_id: string;
    }>;
  };
  subscriptions: {
    sync_event_count: number;
    active_count: number;
    snapshot_count: number;
    canceled_still_entitled: number;
    lifetime_revenue_usd: number;
    revenue_in_range_usd: number;
    lifetime_revenue_nok?: number;
    revenue_in_range_nok?: number;
    by_country: Record<string, { count: number; revenue: number }>;
    top_countries: Array<{ country: string; count: number; revenue: number }>;
    by_store: Record<string, number>;
    by_platform?: Record<string, number>;
    by_platform_test_organic?: Record<string, { test: number; organic: number }>;
    conversion_pct_by_platform?: Record<string, number | null>;
    events_by_type_in_range: Record<string, number>;
    subscribers: Array<Record<string, unknown>>;
    recent_events: Array<Record<string, unknown>>;
    sandbox?: {
      snapshot_count: number;
      lifetime_revenue_usd: number;
      subscribers: Array<Record<string, unknown>>;
    };
  };
  downloads: {
    top_countries: Array<{ country: string; count: number }>;
    by_platform?: Record<string, number>;
    status: string;
    note: string;
    last_synced_at?: string | null;
  };
  admin_actions: Array<Record<string, unknown>>;
  test_accounts?: {
    count: number;
    manage_via_function: string;
  };
  links: Record<string, string>;
};

export function fetchMetrics(range: DateRange) {
  return call<Metrics>("admin-metrics", {
    method: "POST",
    body: JSON.stringify({ from: range.from, to: range.to }),
  });
}

export function syncStoreDownloads() {
  return call<{
    generated_at: string;
    ios: { ok: boolean; source?: string; countries?: number; downloads?: number; error?: string };
    android: { ok: boolean; error?: string };
  }>("sync-store-downloads", { method: "POST" });
}

export function refundAndroid(orderId: string, revoke = true) {
  return call<{ success: boolean; order_id: string }>("admin-refund-android", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId, revoke }),
  });
}

export function manageUser(body: {
  action: "lookup" | "suspend" | "delete";
  email?: string;
  user_id?: string;
  confirm?: string;
}) {
  return call<Record<string, unknown>>("admin-user-manage", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function manageTestAccounts(body: {
  action: "list" | "add" | "remove";
  emails?: string[];
  source?: string;
  note?: string;
}) {
  return call<{
    accounts?: Array<{
      email: string;
      source?: string | null;
      note?: string | null;
      created_at?: string;
    }>;
    added?: number;
    removed?: number;
    emails?: string[];
  }>("admin-test-accounts", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type ProSubscriber = {
  app_user_id: string | null;
  supabase_user_id: string | null;
  email: string | null;
  platform: string;
  store: string | null;
  status: string | null;
  product_id: string | null;
  entitlement_ids: string[] | null;
  country_code: string | null;
  currency: string | null;
  last_price: number | null;
  last_price_in_purchased_currency: number | null;
  will_renew: boolean | null;
  purchased_at: string | null;
  expiration_at: string | null;
  cancel_reason: string | null;
  expiration_reason: string | null;
  lifetime_revenue_usd: number;
  last_event_type: string | null;
  last_event_at: string | null;
  period_type: string | null;
  environment: string | null;
};

export type SubscriberEvent = {
  event_id: string;
  event_type: string;
  app_user_id?: string | null;
  original_app_user_id?: string | null;
  product_id?: string | null;
  store?: string | null;
  country_code?: string | null;
  currency?: string | null;
  price?: number | null;
  price_in_purchased_currency?: number | null;
  takehome_percentage?: number | null;
  tax_percentage?: number | null;
  commission_percentage?: number | null;
  period_type?: string | null;
  event_timestamp?: string | null;
  created_at?: string | null;
  cancel_reason?: string | null;
  expiration_reason?: string | null;
  is_trial_conversion?: boolean | null;
  offer_code?: string | null;
  presented_offering_id?: string | null;
};

export function listProSubscribers(opts?: { includeSandbox?: boolean }) {
  return call<{ count: number; subscribers: ProSubscriber[] }>(
    "admin-subscribers",
    {
      method: "POST",
      body: JSON.stringify({
        action: "list",
        include_sandbox: opts?.includeSandbox === true,
      }),
    },
  );
}

export function subscriberHistory(opts: {
  supabase_user_id?: string;
  app_user_id?: string;
}) {
  return call<{
    subscriber: Record<string, unknown> | null;
    matched_ids: string[];
    count: number;
    events: SubscriberEvent[];
  }>("admin-subscribers", {
    method: "POST",
    body: JSON.stringify({ action: "history", ...opts }),
  });
}

export async function assertAdminAccess(): Promise<boolean> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData.session?.user;
  if (!user) return false;

  const { data, error } = await supabase
    .from("admin_allowlist")
    .select("user_id")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error || !data) {
    await supabase.auth.signOut();
    return false;
  }
  return true;
}

export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function rangePreset(
  preset: "today" | "yesterday" | "7d" | "30d" | "90d" | "all",
): DateRange {
  const to = new Date();
  const from = new Date();
  if (preset === "today") {
    // from === to === today (UTC date keys match isoDate)
  } else if (preset === "yesterday") {
    from.setUTCDate(from.getUTCDate() - 1);
    to.setUTCDate(to.getUTCDate() - 1);
  } else if (preset === "7d") from.setUTCDate(from.getUTCDate() - 6);
  else if (preset === "30d") from.setUTCDate(from.getUTCDate() - 29);
  else if (preset === "90d") from.setUTCDate(from.getUTCDate() - 89);
  else from.setUTCFullYear(2025, 0, 1); // project epoch-ish
  return { from: isoDate(from), to: isoDate(to) };
}
